import React, { KeyboardEvent, useState } from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import LoadingButton from "@atlaskit/button/loading-button";
import { FormFooter } from "@atlaskit/form";
import { ErrorMessage } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Flex, Stack, Box, xcss, media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { PageWorkTypesLeftProps } from "../../../common/types";
import { IssueTypeIcon } from "../../assets/issue-type-svgs";

import messages from "./messages";

export const LeftHandSidePageWorkTypes: React.FC<PageWorkTypesLeftProps> = ({
    defaultIssueTypes,
    selectedIssueTypes,
    onToggleIssueType,
    onClickNext
}) => {
    const { formatMessage } = useIntl();
    const [showError, setShowError] = useState(false);

    const handleNextClick = () => {
        if (selectedIssueTypes.length === 0) {
            setShowError(true);
        } else {
            setShowError(false);
            onClickNext();
        }
    };

    return (
        <Flex
            direction="column"
            justifyContent="space-between"
            xcss={containerStyles}
        >
            <Stack space="space.150">
                <div css={titleCss}>
                    <Heading as="h1" size="xlarge">
                        <FormattedMessage {...messages.chooseWorkTypes} />
                    </Heading>
                </div>
                <p css={subtextStyles}>
                    <FormattedMessage {...messages.workTypesDescription} />
                </p>
                <Box xcss={scrollableContainerStyles} role="list">
                    <Stack space="space.050">
                        {defaultIssueTypes.map((option) => (
                            <Box
                                xcss={[
                                    pressStyles,
                                    selectedIssueTypes.includes(option)
                                        ? activePressStyles
                                        : undefined
                                ]}
                                testId={option.id}
                                key={option.id}
                                onClick={() => {
                                    onToggleIssueType(option);
                                    setShowError(false);
                                }}
                                onKeyDown={(e: KeyboardEvent) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onToggleIssueType(option);
                                        setShowError(false);
                                    }
                                }}
                                role="listitem"
                                tabIndex={0}
                            >
                                <Flex alignItems="center" columnGap="space.150">
                                    <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <IssueTypeIcon type={option.icon} />
                                    </Flex>
                                    <Flex direction="column" alignItems="start">
                                        <span
                                            id={`option-name-${option.name}`}
                                            css={tileTitleStyles}
                                        >
                                            {formatMessage(option.name)}
                                        </span>
                                        <span
                                            id={`option-description-${option.name}`}
                                            css={tileDescriptionStyles}
                                        >
                                            {formatMessage(option.description)}
                                        </span>
                                    </Flex>
                                </Flex>
                            </Box>
                        ))}
                    </Stack>
                </Box>
                {showError && (
                    <ErrorMessage>
                        <FormattedMessage {...messages.issueTypeValidation} />
                    </ErrorMessage>
                )}
                <p css={addMoreLaterStyles}>
                    <FormattedMessage {...messages.addMoreWorkTypesLater} />
                </p>
            </Stack>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <div css={submitButtonCss}>
                        <LoadingButton
                            type="submit"
                            appearance="primary"
                            isLoading={false}
                            onClick={handleNextClick}
                            aria-labelledby="next-button step-label"
                        >
                            <FormattedMessage {...messages.nextButtonCta} />
                        </LoadingButton>
                    </div>
                </Flex>
            </FormFooter>
        </Flex>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const scrollableContainerStyles = xcss({
    [media.above.sm]: {
        maxHeight: "360px",
        overflowY: "auto"
    }
});

const pressStyles = xcss({
    border: "2px solid",
    borderRadius: "border.radius.200",
    borderColor: "color.border",
    backgroundColor: "elevation.surface",
    maxWidth: "100%",
    minHeight: "64px",
    paddingBlock: "space.150",
    paddingInline: "space.200",
    [":hover"]: {
        borderColor: "color.border.focused"
    }
});

const activePressStyles = xcss({
    borderColor: "color.border.focused",
    borderWidth: "border.width.indicator"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});

const submitButtonCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const addMoreLaterStyles = css({
    font: token("font.body.small"),
    color: token("color.text.subtlest"),
    fontVariantNumeric: "slashed-zero",
    margin: 0
});

const tileDescriptionStyles = css({
    font: token("font.body"),
    color: token("color.text.subtlest")
});

const tileTitleStyles = css({
    font: token("font.body"),
    color: token("color.text")
});

const subtextStyles = css({
    font: token("font.body"),
    color: token("color.text.subtle"),
    margin: 0
});

const titleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});
